/**
 * AuditLogViewer — HR / Admin audit trail viewer
 * Route: /audit-logs (add to App.js with ADMIN_HR role)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Badge, Button, Form,
    InputGroup, Spinner, Row, Col,
} from 'react-bootstrap';
import {
    Shield, Search, Download, RefreshCw,
    Filter, ChevronLeft, ChevronRight,
    User, Clock, Monitor,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

// ── Config ────────────────────────────────────────────────────────────────────
const MODULE_COLOURS = {
    payroll:      'success',
    leave:        'primary',
    attendance:   'info',
    employees:    'warning',
    documents:    'secondary',
    helpdesk:     'danger',
    settings:     'dark',
    auth:         'warning',
    performance:  'primary',
    default:      'secondary',
};

const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// ════════════════════════════════════════════════════════════════════════════
const AuditLogViewer = () => {
    const [logs,      setLogs]      = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [total,     setTotal]     = useState(0);
    const [page,      setPage]      = useState(1);
    const [expanded,  setExpanded]  = useState(null);
    const LIMIT = 25;

    // Filters
    const [search,    setSearch]    = useState('');
    const [module,    setModule]    = useState('');
    const [action,    setAction]    = useState('');
    const [dateFrom,  setDateFrom]  = useState('');
    const [dateTo,    setDateTo]    = useState('');
    const [userId,    setUserId]    = useState('');

    const load = useCallback(async (pg = 1) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/hr/audit-logs', {
                params: {
                    page: pg, limit: LIMIT,
                    search:    search    || undefined,
                    module:    module    || undefined,
                    action:    action    || undefined,
                    date_from: dateFrom  || undefined,
                    date_to:   dateTo    || undefined,
                    user_id:   userId    || undefined,
                },
            });
            setLogs(res.data.data?.logs || res.data.data || []);
            setTotal(res.data.data?.total || 0);
            setPage(pg);
        } catch {
            toast.error('Failed to load audit logs');
        } finally { setLoading(false); }
    }, [search, module, action, dateFrom, dateTo, userId]);

    useEffect(() => { load(1); }, []);

    const exportCSV = async () => {
        try {
            const res = await axiosInstance.get('/hr/audit-logs/export', {
                params: { module, action, date_from: dateFrom, date_to: dateTo },
                responseType: 'blob',
            });
            const url  = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href  = url;
            link.setAttribute('download', `audit_logs_${new Date().toISOString().slice(0,10)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch { toast.error('Export failed'); }
    };

    const totalPages = Math.ceil(total / LIMIT);
    const MODULES    = ['payroll','leave','attendance','employees','documents',
                        'helpdesk','settings','auth','performance','expenses'];

    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Shield size={22} className="text-primary"/>
                        Audit Log Viewer
                    </h4>
                    <p className="text-muted small mb-0">
                        Complete trail of all system actions — who did what, when, and from where
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-success" size="sm" onClick={exportCSV}>
                        <Download size={14} className="me-1"/>Export CSV
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => load(1)}>
                        <RefreshCw size={14}/>
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4 p-3">
                <Row className="g-2 align-items-end">
                    <Col xs={12} md={3}>
                        <Form.Label className="small fw-bold mb-1">Search</Form.Label>
                        <InputGroup size="sm">
                            <InputGroup.Text><Search size={12}/></InputGroup.Text>
                            <Form.Control placeholder="User, action, module…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && load(1)}/>
                        </InputGroup>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Label className="small fw-bold mb-1">Module</Form.Label>
                        <Form.Select size="sm" value={module}
                            onChange={e => setModule(e.target.value)}>
                            <option value="">All Modules</option>
                            {MODULES.map(m => (
                                <option key={m} value={m}>
                                    {m.charAt(0).toUpperCase()+m.slice(1)}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Label className="small fw-bold mb-1">From Date</Form.Label>
                        <Form.Control size="sm" type="date" value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}/>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Label className="small fw-bold mb-1">To Date</Form.Label>
                        <Form.Control size="sm" type="date" value={dateTo}
                            onChange={e => setDateTo(e.target.value)}/>
                    </Col>
                    <Col xs={6} md={2}>
                        <Button size="sm" variant="primary" onClick={() => load(1)}
                            className="d-flex align-items-center gap-1">
                            <Filter size={13}/>Apply
                        </Button>
                    </Col>
                    <Col xs={6} md={1}>
                        <Button size="sm" variant="outline-secondary"
                            onClick={() => {
                                setSearch(''); setModule(''); setAction('');
                                setDateFrom(''); setDateTo(''); setUserId('');
                                setTimeout(() => load(1), 0);
                            }}>
                            Clear
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Stats row */}
            <div className="d-flex gap-4 mb-3 small text-muted">
                <span><strong className="text-dark">{total.toLocaleString()}</strong> total records</span>
                <span>Page <strong className="text-dark">{page}</strong> of <strong>{totalPages || 1}</strong></span>
            </div>

            {/* Table */}
            <Card className="border-0 shadow-sm">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary"/>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <Shield size={36} className="mb-2 opacity-30"/>
                        <p>No audit logs found for the selected filters.</p>
                    </div>
                ) : (
                    <Table hover responsive className="mb-0 align-middle" style={{ fontSize: 13 }}>
                        <thead className="bg-light">
                            <tr className="text-uppercase text-muted" style={{ fontSize: 10 }}>
                                <th className="ps-4">User</th>
                                <th>Action</th>
                                <th>Module</th>
                                <th>IP Address</th>
                                <th>Date & Time</th>
                                <th className="pe-4">Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log, i) => (
                                <React.Fragment key={log.id || i}>
                                    <tr>
                                        <td className="ps-4">
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width: 30, height: 30, borderRadius: '50%',
                                                    background: '#eef2ff', color: '#4f46e5',
                                                    display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', fontSize: 12, fontWeight: 700,
                                                    flexShrink: 0,
                                                }}>
                                                    {log.user_name
                                                        ? log.user_name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()
                                                        : <User size={12}/>}
                                                </div>
                                                <div>
                                                    <div className="fw-medium">{log.user_name || 'System'}</div>
                                                    <div className="text-muted" style={{ fontSize: 10 }}>
                                                        {log.user_role || ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <code style={{
                                                background: '#f1f5f9', padding: '2px 8px',
                                                borderRadius: 4, fontSize: 12,
                                            }}>
                                                {log.action}
                                            </code>
                                        </td>
                                        <td>
                                            <Badge bg={MODULE_COLOURS[log.module] || MODULE_COLOURS.default}
                                                style={{ fontSize: 10 }}>
                                                {log.module}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1 text-muted">
                                                <Monitor size={11}/>
                                                <span style={{ fontSize: 12 }}>{log.ip_address || '—'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1 text-muted">
                                                <Clock size={11}/>
                                                <span>{fmtDate(log.created_at)}</span>
                                            </div>
                                        </td>
                                        <td className="pe-4">
                                            {(log.old_values || log.new_values) && (
                                                <Button variant="outline-secondary" size="sm"
                                                    className="py-0 px-2"
                                                    style={{ fontSize: 11 }}
                                                    onClick={() => setExpanded(
                                                        expanded === log.id ? null : log.id
                                                    )}>
                                                    {expanded === log.id ? 'Hide' : 'View'}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                    {/* Expanded details row */}
                                    {expanded === log.id && (
                                        <tr style={{ background: '#f8fafc' }}>
                                            <td colSpan={6} className="px-4 py-3">
                                                <div className="row g-3">
                                                    {log.old_values && (
                                                        <div className="col-md-6">
                                                            <div className="fw-bold small text-muted mb-1">
                                                                BEFORE
                                                            </div>
                                                            <pre style={{
                                                                background: '#fee2e2', borderRadius: 8,
                                                                padding: 12, fontSize: 11,
                                                                maxHeight: 200, overflowY: 'auto',
                                                                margin: 0, whiteSpace: 'pre-wrap',
                                                            }}>
                                                                {typeof log.old_values === 'string'
                                                                    ? JSON.stringify(JSON.parse(log.old_values), null, 2)
                                                                    : JSON.stringify(log.old_values, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                    {log.new_values && (
                                                        <div className={log.old_values ? 'col-md-6' : 'col-12'}>
                                                            <div className="fw-bold small text-muted mb-1">
                                                                {log.old_values ? 'AFTER' : 'DETAILS'}
                                                            </div>
                                                            <pre style={{
                                                                background: '#d1fae5', borderRadius: 8,
                                                                padding: 12, fontSize: 11,
                                                                maxHeight: 200, overflowY: 'auto',
                                                                margin: 0, whiteSpace: 'pre-wrap',
                                                            }}>
                                                                {typeof log.new_values === 'string'
                                                                    ? JSON.stringify(JSON.parse(log.new_values), null, 2)
                                                                    : JSON.stringify(log.new_values, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="text-muted small">
                        Showing {((page-1)*LIMIT)+1}–{Math.min(page*LIMIT, total)} of {total}
                    </span>
                    <div className="d-flex gap-1">
                        <Button size="sm" variant="outline-secondary"
                            disabled={page <= 1}
                            onClick={() => load(page - 1)}>
                            <ChevronLeft size={14}/>
                        </Button>
                        {/* Page number buttons */}
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            const pg = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                            return (
                                <Button key={pg} size="sm"
                                    variant={pg === page ? 'primary' : 'outline-secondary'}
                                    onClick={() => load(pg)}>
                                    {pg}
                                </Button>
                            );
                        })}
                        <Button size="sm" variant="outline-secondary"
                            disabled={page >= totalPages}
                            onClick={() => load(page + 1)}>
                            <ChevronRight size={14}/>
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditLogViewer;
